/**
 * UnifiedWebSocketService - Handles all audio sources with MP3-first approach
 * Supports Zoom, Twilio, and In-Person audio recording with optional real-time transcription
 */

import { getWebSocketUrl } from '../utils/apiUtils';
import { getAuthData } from '../utils/sessionStorage';
import { stopUnifiedAudioSession } from './api';

class UnifiedWebSocketService {
    constructor() {
        this.ws = null;
        this.audioContext = null;
        this.mediaRecorder = null;
        this.audioChunks = [];
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.listeners = {};
        this.isConnected = false;
        this.currentSource = null;
        this.currentCaseId = null;
        this.sessionId = null;
        this.enableRealtime = false;
        
        // Audio processing
        this.audioProcessor = null;
        this.mediaStream = null;
        this.isRecording = false;
        
        // Audio level monitoring
        this.currentAudioLevel = 0;
        this.microphoneStatus = 'inactive'; // inactive, active, muted, error
        this.audioLevelListeners = [];
        
        // Connection state
        this.connectionState = 'disconnected'; // disconnected, connecting, connected, error
        this.lastError = null;
    }
    
    /**
     * Connect to the unified WebSocket endpoint
     * @param {string} source - 'zoom', 'phone', 'in_person'
     * @param {string} caseId - Case ID for the session
     * @param {Object} options - Additional options
     */
    async connect(source, caseId, options = {}) {
        if (this.isConnected && this.currentSource === source && this.currentCaseId === caseId) {
            console.log('[UnifiedWebSocket] Already connected to same source/case');
            return Promise.resolve();
        }
        
        // Close existing connection if any
        if (this.ws) {
            this.disconnect();
        }
        
        this.currentSource = source;
        this.currentCaseId = caseId;
        this.enableRealtime = options.enableRealtime || false;
        
        return new Promise((resolve, reject) => {
            try {
                this.connectionState = 'connecting';
                this.emit('connectionStateChange', { state: 'connecting', source, caseId });
                
                // Get auth token from centralized storage
                const authData = getAuthData();
                const token = authData?.api_token;
                
                if (!token) {
                    throw new Error('No authentication token found. Please ensure you are logged in.');
                }
                
                // Build WebSocket URL using helper function
                const wsPath = `/ws/audio/${source}/${caseId}/?token=${encodeURIComponent(token)}`;
                const wsUrl = getWebSocketUrl(wsPath);
                
                console.log('[UnifiedWebSocket] Connecting to:', wsUrl);
                
                this.ws = new WebSocket(wsUrl);
                
                // Connection timeout
                const connectionTimeout = setTimeout(() => {
                    if (this.connectionState === 'connecting') {
                        console.error('[UnifiedWebSocket] Connection timeout');
                        this.ws.close();
                        this.connectionState = 'error';
                        this.lastError = 'Connection timeout';
                        reject(new Error('Connection timeout'));
                    }
                }, 10000);
                
                this.ws.onopen = () => {
                    console.log('[UnifiedWebSocket] Connected successfully');
                    clearTimeout(connectionTimeout);
                    this.isConnected = true;
                    this.connectionState = 'connected';
                    this.reconnectAttempts = 0;
                    this.lastError = null;
                    
                    this.emit('connectionStateChange', { state: 'connected', source, caseId });
                    
                    // Send session start
                    this.send({
                        type: 'session_start',
                        source,
                        timestamp: new Date().toISOString(),
                        enable_realtime: this.enableRealtime,
                        ...options
                    });
                    
                    resolve();
                };
                
                this.ws.onmessage = (event) => {
                    try {
                        const data = JSON.parse(event.data);
                        this.handleMessage(data);
                    } catch (error) {
                        console.error('[UnifiedWebSocket] Error parsing message:', error);
                    }
                };
                
                this.ws.onerror = (error) => {
                    console.error('[UnifiedWebSocket] WebSocket error:', error);
                    clearTimeout(connectionTimeout);
                    this.connectionState = 'error';
                    this.lastError = 'WebSocket error';
                    this.emit('connectionStateChange', { state: 'error', error });
                    // Don't reject on error - let onclose handle it
                };
                
                this.ws.onclose = (event) => {
                    console.log('[UnifiedWebSocket] Connection closed:', event.code, event.reason);
                    clearTimeout(connectionTimeout);
                    this.isConnected = false;
                    this.connectionState = 'disconnected';
                    
                    this.emit('connectionStateChange', { 
                        state: 'disconnected', 
                        code: event.code, 
                        reason: event.reason 
                    });
                    
                    // Auto-reconnect logic
                    if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
                        this.handleReconnect();
                    }
                };
                
            } catch (error) {
                console.error('[UnifiedWebSocket] Connection error:', error);
                this.connectionState = 'error';
                this.lastError = error.message;
                reject(error);
            }
        });
    }
    
    /**
     * Handle incoming WebSocket messages
     */
    handleMessage(data) {
        console.log('[UnifiedWebSocket] Received message:', data.type);
        
        // Emit to registered listeners
        const listeners = this.listeners[data.type] || [];
        listeners.forEach(callback => {
            try {
                callback(data);
            } catch (error) {
                console.error('[UnifiedWebSocket] Error in message listener:', error);
            }
        });
        
        // Handle specific message types
        switch (data.type) {
            case 'connection_established':
                this.sessionId = data.session_id;
                this.enableRealtime = data.enable_realtime;
                this.emit('sessionEstablished', data);
                break;
                
            case 'recording_started':
                this.emit('recordingStarted', data);
                break;
                
            case 'segment_uploaded':
                this.emit('segmentUploaded', data);
                break;
                
            case 'transcription_update':
                // Always emit transcription updates, UI can decide whether to show them
                console.log('[UnifiedWebSocket] Transcription update received:', data);
                this.emit('transcriptionUpdate', data);
                break;

            case 'realtime_session_started':
                console.log('[UnifiedWebSocket] Real-time session started:', data);
                this.emit('realtimeSessionStarted', data);
                break;

            case 'realtime_transcript':
                console.log('[UnifiedWebSocket] Real-time transcript:', data);
                this.emit('realtimeTranscript', data);
                this.emit('transcriptionUpdate', data); // Also emit as regular transcription for simple display
                break;

            case 'realtime_session_ended':
                console.log('[UnifiedWebSocket] Real-time session ended:', data);
                this.emit('realtimeSessionEnded', data);
                break;

            case 'visit_note_ready':
                this.emit('visitNoteReady', data);
                break;
                
            case 'processing_started':
                this.emit('processingStarted', data);
                break;
                
            case 'note_generation_error':
                this.emit('noteGenerationError', data);
                break;
                
            case 'error':
                console.error('[UnifiedWebSocket] Server error:', data);
                this.emit('serverError', data);
                break;
                
            default:
                console.log('[UnifiedWebSocket] Unhandled message type:', data.type);
        }
    }
    
    /**
     * Start audio capture based on source
     */
    async startAudioCapture() {
        if (this.isRecording) {
            console.log('[UnifiedWebSocket] Already recording');
            return;
        }
        
        try {
            if (this.currentSource === 'in_person') {
                await this.startMicrophoneCapture();
            }
            // For Zoom/Twilio, audio comes from their SDKs
            
            this.isRecording = true;
            this.emit('audioRecordingStarted', { source: this.currentSource });
            
        } catch (error) {
            console.error('[UnifiedWebSocket] Error starting audio capture:', error);
            this.emit('audioRecordingError', { error: error.message });
            throw error;
        }
    }
    
    /**
     * Start microphone capture for in-person sessions
     */
    async startMicrophoneCapture() {
        try {
            // Request microphone access
            this.mediaStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true
                }
            });
            
            // Set up audio context for real-time processing
            // Don't force sample rate - let the browser use the hardware default
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
            
            const source = this.audioContext.createMediaStreamSource(this.mediaStream);
            
            // Create script processor for real-time audio data
            this.audioProcessor = this.audioContext.createScriptProcessor(4096, 1, 1);
            
            this.audioProcessor.onaudioprocess = (event) => {
                if (this.ws?.readyState === WebSocket.OPEN) {
                    const inputData = event.inputBuffer.getChannelData(0);

                    // Calculate audio level for visual feedback
                    let maxLevel = 0;
                    let rmsLevel = 0;
                    for (let i = 0; i < inputData.length; i++) {
                        const sample = Math.abs(inputData[i]);
                        maxLevel = Math.max(maxLevel, sample);
                        rmsLevel += sample * sample;
                    }
                    rmsLevel = Math.sqrt(rmsLevel / inputData.length);

                    // Update current audio level (using RMS for smoother visualization)
                    this.currentAudioLevel = rmsLevel;

                    // Update microphone status based on audio level
                    if (rmsLevel > 0.01) {
                        this.microphoneStatus = 'active';
                    } else if (rmsLevel > 0.001) {
                        this.microphoneStatus = 'active'; // Still consider low levels as active
                    } else {
                        this.microphoneStatus = 'muted'; // Very low or no audio
                    }

                    // Notify audio level listeners
                    this.notifyAudioLevelListeners(rmsLevel);

                    // Log audio level every 100 chunks (roughly every second)
                    if (Math.random() < 0.01) {
                        console.log('[Audio Debug] RMS level:', rmsLevel.toFixed(4), 'Max level:', maxLevel.toFixed(4));
                    }

                    // Convert to 16-bit PCM
                    const pcm16 = new Int16Array(inputData.length);
                    for (let i = 0; i < inputData.length; i++) {
                        pcm16[i] = Math.max(-32768, Math.min(32767, inputData[i] * 32768));
                    }

                    // Send audio data to WebSocket
                    this.ws.send(pcm16.buffer);
                }
            };
            
            source.connect(this.audioProcessor);
            this.audioProcessor.connect(this.audioContext.destination);

            // Also set up MediaRecorder for MP3 backup
            this.mediaRecorder = new MediaRecorder(this.mediaStream, {
                mimeType: 'audio/webm;codecs=opus'
            });
            
            this.audioChunks = [];
            this.mediaRecorder.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    this.audioChunks.push(event.data);
                }
            };
            
            this.mediaRecorder.start(1000); // Collect chunks every second
            
            // Update microphone status
            this.microphoneStatus = 'active';
            
            console.log('[UnifiedWebSocket] Microphone capture started');
            
        } catch (error) {
            console.error('[UnifiedWebSocket] Error starting microphone:', error);
            this.microphoneStatus = 'error';
            throw error;
        }
    }
    
    /**
     * Send audio data from external sources (Zoom/Twilio)
     */
    sendAudioData(audioData) {
        if (this.ws?.readyState === WebSocket.OPEN && this.isRecording) {
            this.ws.send(audioData);
        }
    }
    
    /**
     * Stop audio capture
     */
    async stopAudioCapture() {
        try {
            this.isRecording = false;
            
            // Stop MediaRecorder
            if (this.mediaRecorder && this.mediaRecorder.state === 'recording') {
                this.mediaRecorder.stop();
            }
            
            // Stop audio processing
            if (this.audioProcessor) {
                this.audioProcessor.disconnect();
                this.audioProcessor = null;
            }
            
            // Close audio context
            if (this.audioContext && this.audioContext.state !== 'closed') {
                await this.audioContext.close();
                this.audioContext = null;
            }
            
            // Stop media stream
            if (this.mediaStream) {
                this.mediaStream.getTracks().forEach(track => track.stop());
                this.mediaStream = null;
            }
            
            // Reset audio monitoring
            this.currentAudioLevel = 0;
            this.microphoneStatus = 'inactive';
            this.notifyAudioLevelListeners(0);
            
            this.emit('audioRecordingStopped', { source: this.currentSource });
            console.log('[UnifiedWebSocket] Audio capture stopped');
            
        } catch (error) {
            console.error('[UnifiedWebSocket] Error stopping audio capture:', error);
        }
    }
    
    /**
     * End the current session
     */
    async endSession() {
        try {
            // Save session data BEFORE disconnect clears it
            const savedSessionId = this.sessionId;
            const savedSource = this.currentSource;
            const savedCaseId = this.currentCaseId;

            // Stop audio capture
            await this.stopAudioCapture();

            // Send session end message
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.send({
                    type: 'session_end',
                    timestamp: new Date().toISOString()
                });
            }

            // Close WebSocket
            this.disconnect();

            // Call REST API to trigger batch transcription processing
            if (savedSessionId) {
                try {
                    const authData = getAuthData();
                    const apiToken = authData?.api_token;

                    if (apiToken) {
                        await stopUnifiedAudioSession(apiToken, savedSessionId, {
                            source: savedSource,
                            case_id: savedCaseId
                        });
                        console.log('[UnifiedWebSocket] Successfully triggered batch transcription processing');
                    } else {
                        console.warn('[UnifiedWebSocket] No API token available to stop session');
                    }
                } catch (apiError) {
                    console.error('[UnifiedWebSocket] Error calling stop session API:', apiError);
                    // Don't throw - session end should continue even if API call fails
                }
            }

            this.emit('sessionEnded', { sessionId: savedSessionId });

        } catch (error) {
            console.error('[UnifiedWebSocket] Error ending session:', error);
        }
    }
    
    /**
     * Pause current segment
     */
    pauseSegment() {
        this.send({ 
            type: 'segment_pause',
            timestamp: new Date().toISOString()
        });
    }
    
    /**
     * Resume with new segment
     */
    resumeSegment() {
        this.send({ 
            type: 'segment_resume',
            timestamp: new Date().toISOString()
        });
    }
    
    /**
     * Switch audio source
     */
    switchSource(newSource) {
        this.send({ 
            type: 'source_switch',
            new_source: newSource,
            timestamp: new Date().toISOString()
        });
        this.currentSource = newSource;
    }
    
    /**
     * Trigger note generation
     */
    generateNote() {
        this.send({ type: 'generate_note' });
    }
    
    /**
     * Send message to WebSocket
     */
    send(data) {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify(data));
        } else {
            console.warn('[UnifiedWebSocket] Cannot send message, WebSocket not connected');
        }
    }
    
    /**
     * Register event listener
     */
    on(event, callback) {
        if (!this.listeners[event]) {
            this.listeners[event] = [];
        }
        this.listeners[event].push(callback);
        
        // Return unsubscribe function
        return () => {
            const index = this.listeners[event].indexOf(callback);
            if (index > -1) {
                this.listeners[event].splice(index, 1);
            }
        };
    }
    
    /**
     * Remove event listener (for compatibility)
     */
    off(event, callback) {
        if (!this.listeners[event]) return;
        
        const index = this.listeners[event].indexOf(callback);
        if (index > -1) {
            this.listeners[event].splice(index, 1);
        }
    }
    
    /**
     * Emit event to listeners
     */
    emit(event, data) {
        const listeners = this.listeners[event] || [];
        listeners.forEach(callback => {
            try {
                callback(data);
            } catch (error) {
                console.error('[UnifiedWebSocket] Error in event listener:', error);
            }
        });
    }
    
    /**
     * Handle reconnection with exponential backoff
     */
    handleReconnect() {
        if (this.reconnectAttempts >= this.maxReconnectAttempts) {
            console.error('[UnifiedWebSocket] Max reconnection attempts reached');
            this.emit('reconnectionFailed', { attempts: this.reconnectAttempts });
            return;
        }
        
        const delay = Math.min(1000 * Math.pow(2, this.reconnectAttempts), 30000);
        this.reconnectAttempts++;
        
        console.log(`[UnifiedWebSocket] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
        
        this.emit('reconnecting', { 
            attempt: this.reconnectAttempts, 
            maxAttempts: this.maxReconnectAttempts,
            delay 
        });
        
        setTimeout(() => {
            if (this.currentSource && this.currentCaseId) {
                this.connect(this.currentSource, this.currentCaseId, { 
                    enableRealtime: this.enableRealtime 
                }).catch(error => {
                    console.error('[UnifiedWebSocket] Reconnection failed:', error);
                });
            }
        }, delay);
    }
    
    /**
     * Disconnect WebSocket
     */
    disconnect() {
        if (this.ws) {
            this.ws.close(1000, 'Client disconnect');
            this.ws = null;
        }
        
        this.isConnected = false;
        this.connectionState = 'disconnected';
        this.currentSource = null;
        this.currentCaseId = null;
        this.sessionId = null;
        
        // Stop any ongoing audio capture
        this.stopAudioCapture();
    }
    
    /**
     * Add audio level listener for real-time visualization
     */
    onAudioLevel(callback) {
        this.audioLevelListeners.push(callback);
        
        // Return unsubscribe function
        return () => {
            const index = this.audioLevelListeners.indexOf(callback);
            if (index > -1) {
                this.audioLevelListeners.splice(index, 1);
            }
        };
    }
    
    /**
     * Notify all audio level listeners
     */
    notifyAudioLevelListeners(level) {
        this.audioLevelListeners.forEach(callback => {
            try {
                callback({
                    level,
                    microphoneStatus: this.microphoneStatus,
                    isRecording: this.isRecording
                });
            } catch (error) {
                console.error('[UnifiedWebSocket] Error in audio level listener:', error);
            }
        });
    }
    
    /**
     * Get current audio monitoring data
     */
    getAudioStatus() {
        return {
            currentAudioLevel: this.currentAudioLevel,
            microphoneStatus: this.microphoneStatus,
            isRecording: this.isRecording
        };
    }
    
    /**
     * Get current connection status
     */
    getStatus() {
        return {
            isConnected: this.isConnected,
            connectionState: this.connectionState,
            currentSource: this.currentSource,
            currentCaseId: this.currentCaseId,
            sessionId: this.sessionId,
            enableRealtime: this.enableRealtime,
            isRecording: this.isRecording,
            reconnectAttempts: this.reconnectAttempts,
            lastError: this.lastError,
            // Audio monitoring data
            currentAudioLevel: this.currentAudioLevel,
            microphoneStatus: this.microphoneStatus
        };
    }
}

// Export singleton instance
export default new UnifiedWebSocketService();